import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import gsap from 'gsap';

/**
 * PullToRefresh - A premium mobile-first refresh gesture
 * 
 * Provides a visual indicator and reload trigger when the user pulls down
 * from the top of the page. Essential for standalone PWAs where browser 
 * refresh buttons are absent.
 */
const PullToRefresh = () => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isPulling = useRef(false);
  const containerRef = useRef(null);

  const THRESHOLD = 80; // Distance to trigger refresh
  const MAX_PULL = 120; // Hard limit for visual pull

  useEffect(() => {
    const handleTouchStart = (e) => {
      // Only trigger pull-to-refresh if we're at the very top of the page
      if (window.scrollY > 5) return;
      
      startY.current = e.touches[0].pageY;
      isPulling.current = true;
    };

    const handleTouchMove = (e) => {
      if (!isPulling.current || isRefreshing) return;

      currentY.current = e.touches[0].pageY;
      const diff = currentY.current - startY.current;

      // Only intercept if pulling DOWN with a significant threshold (>15px)
      // AND we're truly at the top of the page
      if (diff > 15 && window.scrollY <= 5) {
        // Prevent default scrolling ONLY during an active pull gesture
        if (e.cancelable) e.preventDefault();
        
        // Logarithmic-style resistance
        const newDist = Math.min(MAX_PULL, diff * 0.4);
        setPullDistance(newDist);
      } else if (diff <= 0) {
        // User is scrolling UP (normal scroll down the page) — let it pass
        isPulling.current = false;
        setPullDistance(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;

      if (pullDistance >= THRESHOLD) {
        triggerRefresh();
      } else {
        // Snap back animation
        if (containerRef.current) {
          gsap.to(containerRef.current, {
            y: -60,
            opacity: 0,
            duration: 0.4,
            ease: 'back.in(1.7)',
            onComplete: () => setPullDistance(0)
          });
        } else {
          setPullDistance(0);
        }
      }
    };

    const triggerRefresh = () => {
      setIsRefreshing(true);
      
      // Animate to "loading" position
      if (containerRef.current) {
        gsap.to(containerRef.current, {
          y: 40,
          opacity: 1,
          duration: 0.3,
          ease: 'power2.out'
        });
      }

      // Vibrate if supported
      if (window.navigator.vibrate) {
        window.navigator.vibrate(10);
      }

      // Reload after a short delay for visual confirmation
      setTimeout(() => {
        window.location.reload();
      }, 800);
    };

    // touchstart must be passive to not interfere with scroll
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    // touchmove must be non-passive ONLY so we can preventDefault during active pull
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pullDistance, isRefreshing]);

  // Derived styles based on pull distance
  const opacity = Math.min(1, pullDistance / (THRESHOLD * 0.8));
  const rotation = (pullDistance / MAX_PULL) * 360;
  const isReady = pullDistance >= THRESHOLD;

  if (pullDistance <= 0 && !isRefreshing) return null;

  return (
    <div 
      ref={containerRef}
      className="ptr-container"
      style={{ 
        transform: `translateY(${isRefreshing ? 40 : pullDistance - 60}px)`,
        opacity: isRefreshing ? 1 : opacity
      }}
    >
      <div 
        className={`ptr-icon ${isRefreshing ? 'spinning' : ''}`}
        style={{ 
          transform: isRefreshing ? 'none' : `rotate(${rotation}deg) scale(${isReady ? 1.1 : 1})`,
          transition: isReady ? 'transform 0.1s cubic-bezier(0.175, 0.885, 0.32, 1.275)' : 'none',
          backgroundColor: isReady ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
          color: isReady ? '#000' : 'white',
          border: isReady ? 'none' : '1px solid rgba(255,255,255,0.2)'
        }}
      >
        <RefreshCw size={20} strokeWidth={isReady ? 3 : 2} />
      </div>
      
      {!isRefreshing && pullDistance > 40 && (
        <span style={{ 
          marginTop: '10px', 
          fontSize: '10px', 
          fontWeight: 800, 
          color: isReady ? 'var(--primary)' : 'rgba(255,255,255,0.4)',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          {isReady ? 'Soltá para actualizar' : 'Tirá para actualizar'}
        </span>
      )}
    </div>
  );
};

export default PullToRefresh;
